import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { addData } from "../services/centralized";

export type Theme = "light" | "dark" | "system";
export type ViewMode = "wireframe" | "solid" | "rendered";
export type PanelLayout = "default" | "minimal" | "fullscreen";
export type ActiveView = "front" | "top" | "side" | "isometric";
export type SelectionMode = "nodes" | "beams" | "all" | null;

interface UIState {
  // Theme and appearance
  theme: Theme;
  viewMode: ViewMode;
  panelLayout: PanelLayout;

  // Mode instructions
  modeInstruction: string | null;

  // Panel states
  leftPanelWidth: number;
  rightPanelWidth: number;
  bottomPanelHeight: number;
  isPanelCollapsed: {
    left: boolean;
    right: boolean;
    bottom: boolean;
  };

  // Modal and dialog states
  activeModal: string | null;
  activeDialog: string | null;

  // Toolbar states
  activeTool: string | null;
  toolbarVisible: boolean;
  statusBarVisible: boolean;

  // Grid and viewport settings
  gridVisible: boolean;
  axesVisible: boolean;
  snapEnabled: boolean;
  orthoMode: boolean;
  // Duplicate tolerance for node creation (same units as coordinates)
  duplicateTolerance: number;

  // ID Label visibility toggles
  showNodeIds: boolean;
  showBeamIds: boolean;

  // Viewport settings
  viewportCount: number;
  activeViewport: number;

  // Loading states
  isLoading: boolean;
  loadingMessage: string;

  // Notifications
  notifications: Array<{
    id: string;
    type: "info" | "warning" | "error" | "success";
    title: string;
    message: string;
    timestamp: number;
    autoHide?: boolean;
  }>;

  // Perpendicular Mode
  isPerpModeActive: boolean;
  selectedBeamForPerp: string | null;
  selectedNodesForPerp: string[];
  showDottedLines: boolean;

  // Context menu / node load state
  contextMenuVisible: boolean;
  contextMenuPosition: { x: number; y: number };
  contextMenuTarget: { type: "node" | "beam" | null; id: string | null };
  selectedNodeId: string | null;
  selectedLoadType: 1 | 2 | null; // 1: Moment, 2: Perpendicular
  // Separate storage for moment (Type1) and perpendicular (Type2) loads per node
  nodeMomentLoads: Record<string, { x: number; y: number; z: number }>;
  nodePerpLoads: Record<string, { x: number; y: number; z: number }>;

  // Enhanced node context menu state
  enhancedNodeContextMenuVisible: boolean;
  enhancedNodeContextMenuPosition: { x: number; y: number };
  enhancedNodeContextMenuNodeId: string | null;

  // Truss configuration panel state
  trussConfigPanelVisible: boolean;
  trussConfigPanelPosition: { x: number; y: number };
  trussConfigPanelNodeId: string | null;

  // Node movement dialog state
  nodeMoveDialogVisible: boolean;
  nodeMoveDialogNodeId: string | null;

  // Enhanced beam context menu state
  enhancedBeamContextMenuVisible: boolean;
  enhancedBeamContextMenuPosition: { x: number; y: number };
  enhancedBeamContextMenuBeamId: string | null;

  // Beam truss configuration panel state
  beamTrussConfigPanelVisible: boolean;
  beamTrussConfigPanelPosition: { x: number; y: number };
  beamTrussConfigPanelBeamId: string | null;

  // Beam properties dialog state
  beamPropertiesDialogVisible: boolean;
  beamPropertiesDialogPosition: { x: number; y: number };
  beamPropertiesDialogBeamId: string | null;

  // BMD (Bending Moment Diagram) input panel state
  bmdInputPanelVisible: boolean;
  bmdInputPanelPosition: { x: number; y: number };
  bmdInputPanelBeamId: string | null;

  // BMD custom data storage
  customBMDData: Record<string, { position: number; moment: number }[]>; // beamId -> BMD points

  // BMD (Bending Moment Diagram) state
  beamBMDVisibility: Record<string, boolean>; // beamId -> visible

  // Section dialog state
  beamSectionDialogVisible: boolean;
  beamSectionDialogPosition: { x: number; y: number };
  beamSectionDialogBeamIds: string[];
  activeView: ActiveView;
  activeSelectionMode: SelectionMode;

  //scale range
  scaleRange: number;
}

interface UIActions {
  // Theme actions
  setTheme: (theme: Theme) => void;
  setViewMode: (mode: ViewMode) => void;
  setPanelLayout: (layout: PanelLayout) => void;

  // Mode instruction action
  setModeInstruction: (instruction: string | null) => void;

  // Panel actions
  setPanelWidth: (panel: "left" | "right", width: number) => void;
  setPanelHeight: (panel: "bottom", height: number) => void;
  togglePanel: (panel: "left" | "right" | "bottom") => void;

  // Modal and dialog actions
  openModal: (modalId: string) => void;
  closeModal: () => void;
  openDialog: (dialogId: string) => void;
  closeDialog: () => void;

  // Tool actions
  setActiveTool: (tool: string | null) => void;
  toggleToolbar: () => void;
  toggleStatusBar: () => void;

  // Viewport actions
  toggleGrid: () => void;
  toggleAxes: () => void;
  toggleSnap: () => void;
  toggleOrtho: () => void;
  setViewportCount: (count: number) => void;
  setActiveViewport: (index: number) => void;

  // ID Label visibility actions
  toggleNodeIds: () => void;
  toggleBeamIds: () => void;

  // Duplicate tolerance
  setDuplicateTolerance: (value: number) => void;

  // Loading actions
  setLoading: (loading: boolean, message?: string) => void;

  // Notification actions
  addNotification: (
    notification: Omit<UIState["notifications"][0], "id" | "timestamp">
  ) => void;
  showNotification: (
    type: "info" | "warning" | "error" | "success",
    title: string,
    message: string
  ) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Perpendicular Mode actions
  setSelectedBeamForPerp: (beamId: string | null) => void;
  addSelectedNodeForPerp: (nodeId: string) => void;
  removeSelectedNodeForPerp: (nodeId: string) => void;
  clearPerpSelections: () => void;
  setShowDottedLines: (show: boolean) => void;
  togglePerpMode: () => void;
  // Context menu actions
  openContextMenu: (
    target: { type: "node" | "beam" | null; id: string | null },
    position?: { x: number; y: number }
  ) => void;
  closeContextMenu: () => void;
  setSelectedNodeId: (nodeId: string | null) => void;
  setSelectedLoadType: (type: 1 | 2 | null) => void;

  // Enhanced node context menu actions
  openEnhancedNodeContextMenu: (
    nodeId: string,
    position: { x: number; y: number }
  ) => void;
  closeEnhancedNodeContextMenu: () => void;

  // Truss configuration panel actions
  openTrussConfigPanel: (
    nodeId: string,
    position: { x: number; y: number }
  ) => void;
  closeTrussConfigPanel: () => void;

  // Node movement dialog actions
  openNodeMoveDialog: (nodeId: string) => void;
  closeNodeMoveDialog: () => void;

  // Node load actions
  setNodeMomentLoad: (
    nodeId: string,
    load: { x: number; y: number; z: number }
  ) => void;
  removeNodeMomentLoad: (nodeId: string) => void;
  setNodePerpLoad: (
    nodeId: string,
    load: { x: number; y: number; z: number }
  ) => void;
  removeNodePerpLoad: (nodeId: string) => void;

  // Enhanced beam context menu actions
  openEnhancedBeamContextMenu: (
    beamId: string,
    position: { x: number; y: number }
  ) => void;
  closeEnhancedBeamContextMenu: () => void;

  // Beam truss configuration panel actions
  openBeamTrussConfigPanel: (
    beamId: string,
    position: { x: number; y: number }
  ) => void;
  closeBeamTrussConfigPanel: () => void;

  // Beam properties dialog actions
  openBeamPropertiesDialog: (
    beamId: string,
    position: { x: number; y: number }
  ) => void;
  closeBeamPropertiesDialog: () => void;

  // BMD input panel actions
  openBMDInputPanel: (
    beamId: string,
    position: { x: number; y: number }
  ) => void;
  closeBMDInputPanel: () => void;
  setCustomBMDData: (
    beamId: string,
    bmdPoints: { position: number; moment: number }[]
  ) => void;

  // BMD (Bending Moment Diagram) actions
  setBeamBMDVisibility: (beamId: string, visible: boolean) => void;

  // Section dialog actions
  openBeamSectionDialog: (
    beamIds: string[],
    position: { x: number; y: number }
  ) => void;
  closeBeamSectionDialog: () => void;
  setActiveView: (view: ActiveView) => void;
  setActiveSelectionMode: (mode: SelectionMode) => void;

  setScaleRange: (value: number) =>void;
}

const initialState: UIState = {
  theme: "system",
  viewMode: "solid",
  panelLayout: "default",

  leftPanelWidth: 280,
  rightPanelWidth: 320,
  bottomPanelHeight: 200,
  isPanelCollapsed: {
    left: false,
    right: false,
    bottom: true,
  },

  activeModal: null,
  activeDialog: null,

  activeTool: null,
  toolbarVisible: true,
  statusBarVisible: true,

  gridVisible: false,
  axesVisible: true,
  snapEnabled: true,
  orthoMode: false,
  duplicateTolerance: 0.01, // default tolerance matching two-decimal rounding

  // ID Label visibility toggles (default off)
  showNodeIds: false,
  showBeamIds: false,

  viewportCount: 1,
  activeViewport: 0,

  isLoading: false,
  loadingMessage: "",

  notifications: [],

  // Mode instructions
  modeInstruction: null,

  // Perpendicular Mode
  isPerpModeActive: false,
  selectedBeamForPerp: null,
  selectedNodesForPerp: [],
  showDottedLines: false,
  // Context menu defaults
  contextMenuVisible: false,
  contextMenuPosition: { x: 0, y: 0 },
  contextMenuTarget: { type: null, id: null },
  selectedNodeId: null,
  selectedLoadType: null,
  nodeMomentLoads: {},
  nodePerpLoads: {},

  // Enhanced node context menu defaults
  enhancedNodeContextMenuVisible: false,
  enhancedNodeContextMenuPosition: { x: 0, y: 0 },
  enhancedNodeContextMenuNodeId: null,

  // Truss configuration panel defaults
  trussConfigPanelVisible: false,
  trussConfigPanelPosition: { x: 0, y: 0 },
  trussConfigPanelNodeId: null,

  // Node movement dialog defaults
  nodeMoveDialogVisible: false,
  nodeMoveDialogNodeId: null,

  // Enhanced beam context menu defaults
  enhancedBeamContextMenuVisible: false,
  enhancedBeamContextMenuPosition: { x: 0, y: 0 },
  enhancedBeamContextMenuBeamId: null,

  // Beam truss configuration panel defaults
  beamTrussConfigPanelVisible: false,
  beamTrussConfigPanelPosition: { x: 0, y: 0 },
  beamTrussConfigPanelBeamId: null,

  // Beam properties dialog defaults
  beamPropertiesDialogVisible: false,
  beamPropertiesDialogPosition: { x: 0, y: 0 },
  beamPropertiesDialogBeamId: null,

  // BMD input panel defaults
  bmdInputPanelVisible: false,
  bmdInputPanelPosition: { x: 0, y: 0 },
  bmdInputPanelBeamId: null,

  // BMD custom data defaults
  customBMDData: {},

  // BMD (Bending Moment Diagram) defaults
  beamBMDVisibility: {},

  // Section dialog defaults
  beamSectionDialogVisible: false,
  beamSectionDialogPosition: { x: 0, y: 0 },
  beamSectionDialogBeamIds: [],
  activeView: "front",
  activeSelectionMode: "all",

  scaleRange: 10,
};

export const useUIStore = create<UIState & UIActions>()(
  devtools(
    immer((set) => ({
      ...initialState,

      // Theme actions
      setTheme: (theme) =>
        set((state) => {
          state.theme = theme;
        }),

      setViewMode: (mode) =>
        set((state) => {
          state.viewMode = mode;
        }),

      setPanelLayout: (layout) =>
        set((state) => {
          state.panelLayout = layout;
        }),

      setModeInstruction: (instruction) =>
        set((state) => {
          state.modeInstruction = instruction;
        }),

      // Panel actions
      setPanelWidth: (panel, width) =>
        set((state) => {
          if (panel === "left") {
            state.leftPanelWidth = Math.max(200, Math.min(600, width));
          } else if (panel === "right") {
            state.rightPanelWidth = Math.max(200, Math.min(600, width));
          }
        }),

      setPanelHeight: (panel, height) =>
        set((state) => {
          if (panel === "bottom") {
            state.bottomPanelHeight = Math.max(150, Math.min(400, height));
          }
        }),

      togglePanel: (panel) =>
        set((state) => {
          state.isPanelCollapsed[panel] = !state.isPanelCollapsed[panel];
        }),

      // Modal and dialog actions
      openModal: (modalId) =>
        set((state) => {
          state.activeModal = modalId;
        }),

      closeModal: () =>
        set((state) => {
          state.activeModal = null;
        }),

      openDialog: (dialogId) =>
        set((state) => {
          state.activeDialog = dialogId;
        }),

      closeDialog: () =>
        set((state) => {
          state.activeDialog = null;
        }),

      // Tool actions
      setActiveTool: (tool) =>
        set((state) => {
          state.activeTool = tool;
        }),

      toggleToolbar: () =>
        set((state) => {
          state.toolbarVisible = !state.toolbarVisible;
        }),

      toggleStatusBar: () =>
        set((state) => {
          state.statusBarVisible = !state.statusBarVisible;
        }),

      // Viewport actions
      toggleGrid: () =>
        set((state) => {
          state.gridVisible = !state.gridVisible;
        }),

      toggleAxes: () =>
        set((state) => {
          state.axesVisible = !state.axesVisible;
        }),

      toggleSnap: () =>
        set((state) => {
          state.snapEnabled = !state.snapEnabled;
        }),

      toggleOrtho: () =>
        set((state) => {
          state.orthoMode = !state.orthoMode;
        }),

      setViewportCount: (count) =>
        set((state) => {
          state.viewportCount = Math.max(1, Math.min(4, count));
          if (state.activeViewport >= count) {
            state.activeViewport = 0;
          }
        }),

      setActiveViewport: (index) =>
        set((state) => {
          if (index >= 0 && index < state.viewportCount) {
            state.activeViewport = index;
          }
        }),

      // ID Label visibility actions
      toggleNodeIds: () =>
        set((state) => {
          state.showNodeIds = !state.showNodeIds;
        }),

      toggleBeamIds: () =>
        set((state) => {
          state.showBeamIds = !state.showBeamIds;
        }),

      // Duplicate tolerance
      setDuplicateTolerance: (value) =>
        set((state) => {
          state.duplicateTolerance = Math.max(0, Number(value) || 0);
        }),

      // Loading actions
      setLoading: (loading, message = "") =>
        set((state) => {
          state.isLoading = loading;
          state.loadingMessage = message;
        }),

      // Notification actions
      addNotification: (notification) =>
        set((state) => {
          const newNotification = {
            ...notification,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            timestamp: Date.now(),
          };
          state.notifications.push(newNotification);

          // Auto-remove after 5 seconds for auto-hide notifications
          if (notification.autoHide !== false) {
            setTimeout(() => {
              set((state) => {
                state.notifications = state.notifications.filter(
                  (n) => n.id !== newNotification.id
                );
              });
            }, 5000);
          }
        }),

      showNotification: (type, title, message) =>
        set((state) => {
          const newNotification = {
            type,
            title,
            message,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            timestamp: Date.now(),
            autoHide: true,
          };
          state.notifications.push(newNotification);

          // Auto-remove after 5 seconds
          setTimeout(() => {
            set((state) => {
              state.notifications = state.notifications.filter(
                (n) => n.id !== newNotification.id
              );
            });
          }, 5000);
        }),

      removeNotification: (id) =>
        set((state) => {
          state.notifications = state.notifications.filter((n) => n.id !== id);
        }),

      clearNotifications: () =>
        set((state) => {
          state.notifications = [];
        }),

      // Perpendicular Mode actions
      setSelectedBeamForPerp: (beamId) =>
        set((state) => {
          state.selectedBeamForPerp = beamId;
        }),
      addSelectedNodeForPerp: (nodeId) =>
        set((state) => {
          if (!state.selectedNodesForPerp.includes(nodeId)) {
            state.selectedNodesForPerp.push(nodeId);
          }
        }),
      removeSelectedNodeForPerp: (nodeId) =>
        set((state) => {
          state.selectedNodesForPerp = state.selectedNodesForPerp.filter(
            (id) => id !== nodeId
          );
        }),
      clearPerpSelections: () =>
        set((state) => {
          state.selectedBeamForPerp = null;
          state.selectedNodesForPerp = [];
          state.showDottedLines = false;
        }),
      setShowDottedLines: (show) =>
        set((state) => {
          state.showDottedLines = show;
        }),
      togglePerpMode: () =>
        set((state) => {
          state.isPerpModeActive = !state.isPerpModeActive;
          if (!state.isPerpModeActive) {
            state.selectedBeamForPerp = null;
            state.selectedNodesForPerp = [];
            state.showDottedLines = false;
          }
        }),

      // Context menu actions
      openContextMenu: (target, position = { x: 0, y: 0 }) =>
        set((state) => {
          state.contextMenuTarget = target;
          state.contextMenuPosition = position;
          state.contextMenuVisible = true;
          state.selectedNodeId =
            target?.type === "node" ? target?.id : state.selectedNodeId;
        }),

      closeContextMenu: () =>
        set((state) => {
          state.contextMenuVisible = false;
          state.contextMenuTarget = { type: null, id: null };
        }),

      setSelectedNodeId: (nodeId) =>
        set((state) => {
          state.selectedNodeId = nodeId;
        }),

      setSelectedLoadType: (type) =>
        set((state) => {
          state.selectedLoadType = type;
        }),

      // Enhanced node context menu actions
      openEnhancedNodeContextMenu: (nodeId, position) =>
        set((state) => {
          state.enhancedNodeContextMenuNodeId = nodeId;
          state.enhancedNodeContextMenuPosition = position;
          state.enhancedNodeContextMenuVisible = true;
        }),

      closeEnhancedNodeContextMenu: () =>
        set((state) => {
          state.enhancedNodeContextMenuVisible = false;
          state.enhancedNodeContextMenuNodeId = null;
        }),

      // Truss configuration panel actions
      openTrussConfigPanel: (nodeId, position) =>
        set((state) => {
          state.trussConfigPanelNodeId = nodeId;
          state.trussConfigPanelPosition = position;
          state.trussConfigPanelVisible = true;
          // Close enhanced context menu when opening truss panel
          state.enhancedNodeContextMenuVisible = false;
        }),

      closeTrussConfigPanel: () =>
        set((state) => {
          state.trussConfigPanelVisible = false;
          state.trussConfigPanelNodeId = null;
        }),

      // Node movement dialog actions
      openNodeMoveDialog: (nodeId) =>
        set((state) => {
          state.nodeMoveDialogNodeId = nodeId;
          state.nodeMoveDialogVisible = true;
          // Close enhanced context menu when opening move dialog
          state.enhancedNodeContextMenuVisible = false;
        }),

      closeNodeMoveDialog: () =>
        set((state) => {
          state.nodeMoveDialogVisible = false;
          state.nodeMoveDialogNodeId = null;
        }),

      // Node load management - separate maps for Type 1 (moment) and Type 2 (perpendicular)
      setNodeMomentLoad: (nodeId, load) =>
        set((state) => {
          state.nodeMomentLoads[nodeId] = load;
        }),

      removeNodeMomentLoad: (nodeId) =>
        set((state) => {
          delete state.nodeMomentLoads[nodeId];
        }),

      setNodePerpLoad: (nodeId, load) =>
        set((state) => {
          state.nodePerpLoads[nodeId] = load;
        }),

      removeNodePerpLoad: (nodeId) =>
        set((state) => {
          delete state.nodePerpLoads[nodeId];
        }),

      // Enhanced beam context menu actions
      openEnhancedBeamContextMenu: (beamId, position) =>
        set((state) => {
          state.enhancedBeamContextMenuBeamId = beamId;
          state.enhancedBeamContextMenuPosition = position;
          state.enhancedBeamContextMenuVisible = true;
        }),

      closeEnhancedBeamContextMenu: () =>
        set((state) => {
          state.enhancedBeamContextMenuVisible = false;
          state.enhancedBeamContextMenuBeamId = null;
        }),

      // Beam truss configuration panel actions
      openBeamTrussConfigPanel: (beamId, position) =>
        set((state) => {
          state.beamTrussConfigPanelBeamId = beamId;
          state.beamTrussConfigPanelPosition = position;
          state.beamTrussConfigPanelVisible = true;
          // Close enhanced context menu when opening beam truss panel
          state.enhancedBeamContextMenuVisible = false;
        }),

      closeBeamTrussConfigPanel: () =>
        set((state) => {
          state.beamTrussConfigPanelVisible = false;
          state.beamTrussConfigPanelBeamId = null;
        }),

      // Beam properties dialog actions
      openBeamPropertiesDialog: (beamId, position) =>
        set((state) => {
          state.beamPropertiesDialogBeamId = beamId;
          state.beamPropertiesDialogPosition = position;
          state.beamPropertiesDialogVisible = true;
          // Close enhanced context menu when opening beam properties dialog
          state.enhancedBeamContextMenuVisible = false;
        }),

      closeBeamPropertiesDialog: () =>
        set((state) => {
          state.beamPropertiesDialogVisible = false;
          state.beamPropertiesDialogBeamId = null;
        }),

      // BMD input panel actions
      openBMDInputPanel: (beamId, position) =>
        set((state) => {
          state.bmdInputPanelBeamId = beamId;
          state.bmdInputPanelPosition = position;
          state.bmdInputPanelVisible = true;
          // Close enhanced context menu when opening BMD input panel
          state.enhancedBeamContextMenuVisible = false;
        }),

      closeBMDInputPanel: () =>
        set((state) => {
          state.bmdInputPanelVisible = false;
          state.bmdInputPanelBeamId = null;
        }),

      setCustomBMDData: (beamId, bmdPoints) =>
        set((state) => {
          state.customBMDData[beamId] = bmdPoints;
          // Also make BMD visible when data is set
          state.beamBMDVisibility[beamId] = true;
        }),

      // BMD (Bending Moment Diagram) actions
      setBeamBMDVisibility: (beamId, visible) =>
        set((state) => {
          state.beamBMDVisibility[beamId] = visible;
        }),

      // Section dialog actions
      openBeamSectionDialog: (
        beamIds: string[],
        position: { x: number; y: number }
      ) =>
        set((state) => {
          state.beamSectionDialogBeamIds = beamIds;
          state.beamSectionDialogPosition = position;
          state.beamSectionDialogVisible = true;
          // Close enhanced context menu when opening section dialog
          state.enhancedBeamContextMenuVisible = false;
        }),

      closeBeamSectionDialog: () =>
        set((state) => {
          state.beamSectionDialogVisible = false;
          state.beamSectionDialogBeamIds = [];
        }),

      setActiveView: (view) =>
        set((state) => {
          state.activeView = view;
        }),

      setActiveSelectionMode: (mode) =>
        set((state) => {
          state.activeSelectionMode = mode;
        }),

      setScaleRange: (value: number) => set({ scaleRange: value }),
    })),
    {
      name: "ui-store",
    }
  )
);

// Subscribe to UIStore and publish load data to centralized store
useUIStore.subscribe((state) => {
  addData("nodeMomentLoads", state.nodeMomentLoads);
  addData("nodePerpLoads", state.nodePerpLoads);
  const momentCount = Object.keys(state.nodeMomentLoads).length;
  const perpCount = Object.keys(state.nodePerpLoads).length;
  console.log(
    "✅ [UIStore→Centralized] Published loads - Moment:",
    momentCount,
    "Perp:",
    perpCount
  );
});
